import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import remarkBreaks from "remark-breaks"
import rehypeRaw from "rehype-raw"
import { cn } from "@/lib/cn"

type Props = {
  content: string
  /** When true, link clicks are neutralized — used for system rating prompts
   *  rendered on the operator side, where only the end user should be able
   *  to fire the link. */
  neutralizeLinks?: boolean
}

// Swap `[Recommended]` (anywhere in the text — Forge uses it on diagnose
// options) for a styled inline badge. rehypeRaw renders the injected
// HTML. Case-insensitive; matches the literal token only so authored
// text like `[recommended action]` is left alone. Styled in Forge's
// signature gold — same palette as the Forge button + sparkle icon, so
// the recommendation badge reads as a Forge-authored signal.
const RECOMMENDED_BADGE =
  '<span class="ml-1 inline-flex items-center rounded-full border border-vip-goldBorder bg-vip-goldMute px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-vip-goldSoft shadow-goldGlow align-middle">Recommended</span>'

function injectBadges(text: string): string {
  return text.replace(/\[recommended\]/gi, RECOMMENDED_BADGE)
}

export function MessageMarkdown({ content, neutralizeLinks = false }: Props) {
  return (
    <div className="prose-chat">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeRaw]}
        components={{
          a: ({ node: _node, href, children, ...props }) => (
            neutralizeLinks
              ? <span {...props} className="text-app-muted">{children}</span>
              : <a {...props} href={href} target="_blank" rel="noopener noreferrer" className="text-brand-red underline-offset-2 hover:underline">{children}</a>
          ),
          code: ({ node: _node, className, children, ...props }) => (
            <code {...props} className={cn("rounded bg-black/30 px-1 py-0.5 font-mono text-[0.85em]", className)}>{children}</code>
          ),
          pre: ({ node: _node, ...props }) => (
            <pre {...props} className="my-2 overflow-x-auto rounded-control bg-black/30 p-2 font-mono text-[12px]" />
          ),
          // Bumped vertical rhythm: paragraphs and lists need real
          // breathing room when Forge writes 3-4 numbered options with
          // diagnostic prose between them. Previously my-0 / my-1
          // collapsed multi-paragraph replies into a wall of text.
          ul: ({ node: _node, ...props }) => <ul {...props} className="my-2 list-disc pl-5 space-y-1" />,
          ol: ({ node: _node, ...props }) => <ol {...props} className="my-2 list-decimal pl-5 space-y-1" />,
          li: ({ node: _node, ...props }) => <li {...props} className="leading-relaxed" />,
          p:  ({ node: _node, ...props }) => <p {...props} className="my-2 leading-relaxed first:mt-0 last:mb-0" />,
          h1: ({ node: _node, ...props }) => <h3 {...props} className="mt-3 mb-1 text-sm font-semibold text-app-text first:mt-0" />,
          h2: ({ node: _node, ...props }) => <h3 {...props} className="mt-3 mb-1 text-sm font-semibold text-app-text first:mt-0" />,
          h3: ({ node: _node, ...props }) => <h3 {...props} className="mt-3 mb-1 text-sm font-semibold text-app-text first:mt-0" />,
          blockquote: ({ node: _node, ...props }) => (
            <blockquote {...props} className="my-2 border-l-2 border-brand-red/40 pl-3 italic text-app-muted" />
          ),
        }}
      >
        {injectBadges(content)}
      </ReactMarkdown>
    </div>
  )
}
